import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Paintbrush, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { TableBlock as TableBlockType } from "@/canvas/types/blocks";
import { useCanvasStore } from "@/canvas/store";
import { cn } from "@/lib/utils";
import {
  TableRowWithBackground,
  type RowBackgroundColor,
} from "@/canvas/extensions/tableRowBackground";

interface TableBlockProps {
  block: TableBlockType;
  readOnly?: boolean;
}

const COLOR_SWATCHES: { value: RowBackgroundColor; bg: string; label: string }[] = [
  { value: null, bg: "transparent", label: "Ingen markering" },
  { value: "danger", bg: "#ef4444", label: "Fara" },
  { value: "warning", bg: "#f59e0b", label: "Varning" },
  { value: "success", bg: "#22c55e", label: "Godkänd" },
];

export function TableBlock({ block, readOnly = false }: TableBlockProps) {
  const { t } = useTranslation();
  const updateBlockContent = useCanvasStore((state) => state.updateBlockContent);
  const containerRef = useRef<HTMLDivElement>(null);

  // Hover tracking for overlay buttons
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);
  const [rowRect, setRowRect] = useState<{ top: number; height: number } | null>(null);
  const [colRect, setColRect] = useState<{ left: number; width: number } | null>(null);
  const [tableTop, setTableTop] = useState(0);
  const [activeRowColor, setActiveRowColor] = useState<RowBackgroundColor>(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const hoverClearTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: { class: "border-collapse w-full" },
      }),
      TableRowWithBackground.configure({
        HTMLAttributes: { class: "border-b border-border" },
      }),
      TableHeader.configure({
        HTMLAttributes: { class: "bg-muted/50 text-left p-2 border border-border font-medium" },
      }),
      TableCell.configure({
        HTMLAttributes: { class: "p-2 border border-border align-top" },
      }),
    ],
    content: block.content.tiptapContent,
    editorProps: {
      attributes: { class: "focus:outline-none" },
    },
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      updateBlockContent(block.id, {
        tiptapContent: json as Record<string, unknown>,
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    },
  });

  useEffect(() => {
    if (editor) editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  const addRowBefore = useCallback(() => editor?.chain().focus().addRowBefore().run(), [editor]);
  const addRowAfter = useCallback(() => editor?.chain().focus().addRowAfter().run(), [editor]);
  const addColumnBefore = useCallback(
    () => editor?.chain().focus().addColumnBefore().run(),
    [editor],
  );
  const addColumnAfter = useCallback(
    () => editor?.chain().focus().addColumnAfter().run(),
    [editor],
  );

  const cancelHoverClear = useCallback(() => {
    if (hoverClearTimeout.current) {
      clearTimeout(hoverClearTimeout.current);
      hoverClearTimeout.current = null;
    }
  }, []);

  const scheduleHoverClear = useCallback(() => {
    cancelHoverClear();
    hoverClearTimeout.current = setTimeout(() => {
      setHoveredRow(null);
      setHoveredCol(null);
      setColorPickerOpen(false);
      hoverClearTimeout.current = null;
    }, 200);
  }, [cancelHoverClear]);

  useEffect(() => () => cancelHoverClear(), [cancelHoverClear]);

  /** Read the current backgroundColor attribute from the tableRow node at rowIndex. */
  const readRowColor = useCallback(
    (rowIndex: number): RowBackgroundColor => {
      if (!editor) return null;
      const table = containerRef.current?.querySelector("table");
      if (!table) return null;
      const rows = table.querySelectorAll("tr");
      const targetRow = rows[rowIndex];
      if (!targetRow) return null;
      const firstCell = targetRow.querySelector("td, th") as HTMLElement | null;
      if (!firstCell) return null;
      const cellPos = editor.view.posAtDOM(firstCell, 0);
      const $pos = editor.state.doc.resolve(cellPos);
      for (let d = $pos.depth; d > 0; d--) {
        const n = $pos.node(d);
        if (n.type.name === "tableRow")
          return (n.attrs.backgroundColor as RowBackgroundColor) ?? null;
      }
      return null;
    },
    [editor],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("button")) {
        cancelHoverClear();
        return;
      }
      const cell = target.closest("td, th") as HTMLTableCellElement;
      if (!cell) {
        scheduleHoverClear();
        return;
      }
      cancelHoverClear();

      const tr = cell.closest("tr");
      if (!tr?.parentElement) return;
      const container = containerRef.current;
      if (!container) return;

      const rowIndex = Array.from(tr.parentElement.children).indexOf(tr);
      const colIndex = cell.cellIndex;
      if (rowIndex === hoveredRow && colIndex === hoveredCol) return;

      const containerRect = container.getBoundingClientRect();
      const trRect = tr.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();

      const tableWrapper = container.querySelector(".table-block-editor");
      if (tableWrapper) {
        setTableTop(tableWrapper.getBoundingClientRect().top - containerRect.top);
      }

      setHoveredRow(rowIndex);
      setHoveredCol(colIndex);
      setRowRect({ top: trRect.top - containerRect.top, height: trRect.height });
      setColRect({ left: cellRect.left - containerRect.left, width: cellRect.width });
      setColorPickerOpen(false);
      setActiveRowColor(readRowColor(rowIndex));
    },
    [hoveredRow, hoveredCol, cancelHoverClear, scheduleHoverClear, readRowColor],
  );

  const handleMouseLeave = useCallback(() => scheduleHoverClear(), [scheduleHoverClear]);

  const getTableDimensions = useCallback(() => {
    const tableEl = containerRef.current?.querySelector("table");
    if (!tableEl) return { rows: 0, cols: 0 };
    const rows = tableEl.querySelectorAll("tr");
    const cols = rows[0]?.children.length || 0;
    return { rows: rows.length, cols };
  }, []);

  const handleDeleteRow = useCallback(
    (rowIndex: number) => {
      if (!editor) return;
      const table = containerRef.current?.querySelector("table");
      if (!table) return;
      const rows = table.querySelectorAll("tr");
      const targetRow = rows[rowIndex];
      if (!targetRow) return;
      const firstCell = targetRow.querySelector("td, th");
      if (!firstCell) return;
      const pos = editor.view.posAtDOM(firstCell, 0);
      editor.chain().focus().setTextSelection(pos).deleteRow().run();
      setHoveredRow(null);
    },
    [editor],
  );

  const handleDeleteCol = useCallback(
    (colIndex: number) => {
      if (!editor) return;
      const table = containerRef.current?.querySelector("table");
      if (!table) return;
      const firstRow = table.querySelector("tr");
      if (!firstRow) return;
      const targetCell = firstRow.children[colIndex] as HTMLElement;
      if (!targetCell) return;
      const pos = editor.view.posAtDOM(targetCell, 0);
      editor.chain().focus().setTextSelection(pos).deleteColumn().run();
      setHoveredCol(null);
    },
    [editor],
  );

  const handleSetRowColor = useCallback(
    (rowIndex: number, color: RowBackgroundColor) => {
      if (!editor) return;
      const table = containerRef.current?.querySelector("table");
      if (!table) return;
      const rows = table.querySelectorAll("tr");
      const targetRow = rows[rowIndex];
      if (!targetRow) return;
      const firstCell = targetRow.querySelector("td, th");
      if (!firstCell) return;
      const pos = editor.view.posAtDOM(firstCell, 0);
      editor
        .chain()
        .focus()
        .setTextSelection(pos)
        .updateAttributes("tableRow", { backgroundColor: color })
        .run();
      setActiveRowColor(color);
      setColorPickerOpen(false);
    },
    [editor],
  );

  if (!editor) return null;

  const { rows: rowCount, cols: colCount } = getTableDimensions();

  return (
    <div
      ref={containerRef}
      className={cn("relative", !readOnly && "pt-5 pl-12")}
      onMouseMove={!readOnly ? handleMouseMove : undefined}
      onMouseLeave={!readOnly ? handleMouseLeave : undefined}
    >
      <div className="group">
        {/* Toolbar: add row/column buttons */}
        {!readOnly && (
          <div className="absolute -top-8 left-0 right-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity bg-background/80 backdrop-blur-sm rounded-md p-1 shadow-sm border z-10">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs gap-1"
              onClick={addRowBefore}
              title={t("canvas.blocks.table.addRowBefore", "Add row above")}
            >
              <Plus className="h-3 w-3" />
              {t("canvas.blocks.table.row", "Row")} &#8593;
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs gap-1"
              onClick={addRowAfter}
              title={t("canvas.blocks.table.addRowAfter", "Add row below")}
            >
              <Plus className="h-3 w-3" />
              {t("canvas.blocks.table.row", "Row")} &#8595;
            </Button>
            <div className="w-px h-4 bg-border mx-1" />
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs gap-1"
              onClick={addColumnBefore}
              title={t("canvas.blocks.table.addColumnBefore", "Add column left")}
            >
              <Plus className="h-3 w-3" />
              {t("canvas.blocks.table.column", "Col")} &#8592;
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs gap-1"
              onClick={addColumnAfter}
              title={t("canvas.blocks.table.addColumnAfter", "Add column right")}
            >
              <Plus className="h-3 w-3" />
              {t("canvas.blocks.table.column", "Col")} &#8594;
            </Button>
          </div>
        )}

        {/* Table editor */}
        <div
          className={cn("rounded-md border border-border overflow-x-auto", "table-block-editor")}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      {/* Row color + delete overlay — shown when hovering a row */}
      {!readOnly && hoveredRow !== null && rowRect && (
        <>
          {/* Color circle */}
          <button
            className="absolute z-20 flex items-center justify-center w-6 h-6 rounded-full bg-background shadow text-muted-foreground hover:text-foreground transition-colors outline-none"
            style={{
              left: 0,
              top: rowRect.top + rowRect.height / 2 - 12,
            }}
            aria-label={t("canvas.blocks.table.rowColor", "Row color")}
            title={t("canvas.blocks.table.rowColor", "Row color")}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => setColorPickerOpen((v) => !v)}
          >
            <Paintbrush
              className="h-3 w-3"
              style={
                activeRowColor
                  ? { color: COLOR_SWATCHES.find((s) => s.value === activeRowColor)?.bg }
                  : undefined
              }
            />
          </button>

          {/* Inline color picker — appears below the color circle */}
          {colorPickerOpen && (
            <div
              className="absolute z-30 flex items-center gap-1 p-1.5 rounded-lg bg-background border shadow-md"
              style={{
                left: 0,
                top: rowRect.top + rowRect.height / 2 + 16,
              }}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={cancelHoverClear}
            >
              {COLOR_SWATCHES.map(({ value, bg, label }) => (
                <button
                  key={String(value)}
                  className={cn(
                    "w-5 h-5 rounded-full border-2 transition-all outline-none",
                    activeRowColor === value
                      ? "border-foreground scale-110"
                      : "border-transparent hover:border-muted-foreground/40 hover:scale-105",
                    value === null && "bg-background border-border",
                  )}
                  style={value ? { backgroundColor: bg } : {}}
                  title={label}
                  aria-label={label}
                  onClick={() => handleSetRowColor(hoveredRow, value)}
                >
                  {value === null && (
                    <span className="block w-3 h-px bg-muted-foreground/40 rotate-45 mx-auto" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Delete row circle */}
          {rowCount > 1 && (
            <button
              className="absolute z-20 flex items-center justify-center w-6 h-6 rounded-full bg-background shadow hover:text-destructive text-muted-foreground transition-colors outline-none"
              style={{
                left: 28,
                top: rowRect.top + rowRect.height / 2 - 12,
              }}
              aria-label={t("canvas.blocks.table.deleteRow", "Delete row")}
              title={t("canvas.blocks.table.deleteRow", "Delete row")}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={() => handleDeleteRow(hoveredRow)}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </>
      )}

      {/* Column delete overlay */}
      {!readOnly && hoveredCol !== null && colRect && colCount > 1 && (
        <button
          className="absolute z-20 flex items-center justify-center w-6 h-6 rounded-full bg-background shadow hover:text-destructive text-muted-foreground transition-colors outline-none"
          style={{
            top: tableTop - 24,
            left: colRect.left + colRect.width / 2 - 12,
          }}
          aria-label={t("canvas.blocks.table.deleteColumn", "Delete column")}
          title={t("canvas.blocks.table.deleteColumn", "Delete column")}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={() => handleDeleteCol(hoveredCol)}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
